import type { ReactNode } from 'react';
import type { AppError } from '../../core/error/appError';
import type { UiState } from '../../state/uiState';
import { S } from '../../i18n/strings';
import { actionLabel, presentError } from './errorPresentation';
import { AlertIcon } from './icons';

/**
 * Renderiza los cuatro estados de una pantalla en un solo lugar.
 *
 * Al recibir el `UiState` completo, olvidarse del caso vacío o del de error deja de ser posible:
 * el tipo obliga a cubrirlos y esta pieza los pinta igual en toda la aplicación.
 */
export function StateContainer<T>({
  state,
  loading,
  empty,
  onRetry,
  onErrorAction,
  children,
}: {
  state: UiState<T>;
  loading?: ReactNode;
  empty?: ReactNode;
  onRetry?: () => void;
  onErrorAction?: (error: AppError) => void;
  children: (data: T) => ReactNode;
}) {
  switch (state.status) {
    case 'LOADING':
      return <>{loading ?? <SkeletonList />}</>;
    case 'EMPTY':
      return <>{empty ?? <EmptyState title={S.searchEmptyTitle} body={S.searchEmptyBody} />}</>;
    case 'ERROR':
      return <ErrorState error={state.error} onRetry={onRetry} onAction={onErrorAction} />;
    case 'SUCCESS':
      return <>{children(state.data)}</>;
  }
}

export function EmptyState({
  icon = '🔎',
  title,
  body,
  action,
}: {
  icon?: string;
  title: string;
  body?: string | null;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="state">
      <div className="state__icon" aria-hidden="true">
        {icon}
      </div>
      <p className="state__title">{title}</p>
      {body && <p className="state__body">{body}</p>}
      {action && (
        <button type="button" className="btn btn--outline" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  onAction,
}: {
  error: AppError;
  onRetry?: () => void;
  onAction?: (error: AppError) => void;
}) {
  const presentation = presentError(error);
  // La acción se ofrece solo si existe y hay quien la atienda: un botón "Reintentar" que no
  // reintenta nada es peor que no tener botón.
  const handler =
    presentation.action === 'RETRY'
      ? onRetry
      : presentation.action && onAction
        ? () => onAction(error)
        : undefined;

  return (
    <div className="state" role="alert">
      <div className="state__icon" aria-hidden="true">
        <AlertIcon size={34} />
      </div>
      <p className="state__title">{presentation.title}</p>
      {presentation.body && <p className="state__body">{presentation.body}</p>}
      {presentation.action && handler && (
        <button type="button" className="btn" onClick={handler}>
          {actionLabel(presentation.action)}
        </button>
      )}
    </div>
  );
}

export function ErrorBanner({ error, onRetry }: { error: AppError; onRetry?: () => void }) {
  const presentation = presentError(error);
  return (
    <div className="banner" role="alert">
      <AlertIcon size={18} />
      <span>{presentation.body ?? presentation.title}</span>
      {presentation.action === 'RETRY' && onRetry && (
        <button type="button" className="btn btn--text" onClick={onRetry}>
          {S.actionRetry}
        </button>
      )}
    </div>
  );
}

export function SkeletonList({ count = 4 }: { count?: number }) {
  return (
    <div className="restaurant-grid" aria-busy="true" aria-label={S.loading}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="card">
          <div className="skeleton" style={{ aspectRatio: '4 / 3', borderRadius: 0 }} />
          <div className="stack stack--tight" style={{ padding: 14 }}>
            <div className="skeleton" style={{ height: 16, width: '70%' }} />
            <div className="skeleton" style={{ height: 12, width: '45%' }} />
            <div className="skeleton" style={{ height: 12, width: '55%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonBlock({ height = 120 }: { height?: number }) {
  return <div className="skeleton" style={{ height }} aria-busy="true" aria-label={S.loading} />;
}
